import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { motion } from "framer-motion";
import {
  Crown,
  Mic2,
  Headphones,
  CalendarDays,
  Music4,
  Instagram,
  Facebook,
  Youtube,
  Menu,
  X,
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  ArrowRight,
  CalendarPlus,
  Heart,
} from "lucide-react";
import { api } from "@/lib/api";

const DJ_PHOTO =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_dj-song-bookings/artifacts/med9g9p3_20260911_220123.webp";
const CROWD_IMG =
  "https://images.unsplash.com/photo-1620302593918-37d23c82d770?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const TikTok = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M16.5 3c.3 2 1.6 3.6 3.5 3.9v2.6c-1.3 0-2.5-.4-3.6-1v6.7a5.7 5.7 0 1 1-5.7-5.7c.3 0 .6 0 .9.1v2.7a3 3 0 1 0 2.1 2.9V3h2.8Z" />
  </svg>
);

const NAV = [
  { label: "HOME", href: "#home" },
  { label: "KARAOKE", href: "#servizi" },
  { label: "EVENTI", href: "#eventi" },
  { label: "PRENOTA DJ", href: "#servizi" },
  { label: "GALLERY", href: "#eventi" },
  { label: "CONTATTI", href: "#contatti" },
];

const SERVICES = [
  { icon: Mic2, color: "text-pink-400", ring: "border-pink-500/30", label: "KARAOKE", desc: "Scegli la tua canzone dal tuo smartphone tramite QR Code", to: "/prenota" },
  { icon: Headphones, color: "text-blue-400", ring: "border-blue-500/30", label: "DJ SET", desc: "Musica per ogni occasione dai party privati ai locali", href: "#contatti" },
  { icon: CalendarDays, color: "text-fuchsia-400", ring: "border-fuchsia-500/30", label: "EVENTI", desc: "Serate, feste, compleanni, matrimoni, eventi aziendali", href: "#eventi" },
  { icon: Music4, color: "text-emerald-400", ring: "border-emerald-500/30", label: "PRENOTA DJ JEKO", desc: "Richiedi un preventivo per il tuo evento", href: "#contatti" },
];

const DEFAULT_EVENTS = [
  { d: "26", m: "LUG", w: "SAB", title: "KARAOKE NIGHT", place: "Lido Villa Fiorita - Ostia" },
  { d: "01", m: "AGO", w: "VEN", title: "SUMMER PARTY", place: "Torvaianica Beach" },
  { d: "08", m: "AGO", w: "SAB", title: "KARAOKE & DANCE", place: "Exe Beach - Torvaianica" },
];

const MESI = ["GEN", "FEB", "MAR", "APR", "MAG", "GIU", "LUG", "AGO", "SET", "OTT", "NOV", "DIC"];
const GIORNI = ["DOM", "LUN", "MAR", "MER", "GIO", "VEN", "SAB"];

function formatEvent(e) {
  const dt = e.date ? new Date(e.date + "T00:00:00") : null;
  const valid = dt && !isNaN(dt.getTime());
  return {
    d: valid ? String(dt.getDate()).padStart(2, "0") : "--",
    m: valid ? MESI[dt.getMonth()] : "",
    w: valid ? GIORNI[dt.getDay()] : "",
    title: e.title,
    place: e.place || "",
  };
}

export default function HomePage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [eventName, setEventName] = useState("Karaoke Night");
  const [events, setEvents] = useState(DEFAULT_EVENTS);
  const [contacts, setContacts] = useState(null);
  const [heroSrc, setHeroSrc] = useState(DJ_PHOTO);
  const [bannerSrc, setBannerSrc] = useState(CROWD_IMG);
  const bookingUrl = `${window.location.origin}/prenota`;

  useEffect(() => {
    api.get("/event").then(({ data }) => setEventName(data.event_name)).catch(() => {});
    api
      .get("/events")
      .then(({ data }) => {
        if (Array.isArray(data) && data.length > 0) setEvents(data.map(formatEvent));
      })
      .catch(() => {});
    api
      .get("/home")
      .then(({ data }) => {
        setContacts(data.contacts);
        if (data.hero_version) {
          setHeroSrc(
            `${process.env.REACT_APP_BACKEND_URL}/api/media/hero?v=${encodeURIComponent(data.hero_version)}`
          );
        }
        if (data.banner_version) {
          setBannerSrc(
            `${process.env.REACT_APP_BACKEND_URL}/api/media/banner?v=${encodeURIComponent(data.banner_version)}`
          );
        }
      })
      .catch(() => {});
  }, []);

  const c = contacts || {};
  const waNumber = (c.whatsapp || "").replace(/[^0-9]/g, "");

  return (
    <div id="home" className="jeko-bg min-h-screen w-full text-white overflow-x-hidden">
      {/* ===== Header ===== */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <a href="#home" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="leading-none">
              <span className="font-brush text-2xl sm:text-3xl tracking-wide">
                DJ <span className="neon-text-magenta">JEKO</span>
              </span>
              <p className="font-mono text-[9px] tracking-[0.35em] text-slate-400 mt-0.5">
                MUSIC IS LIFE
              </p>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.href}
                className="text-sm font-medium text-slate-200 hover:text-pink-400 transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 hover:after:w-full after:bg-pink-500 after:transition-all"
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-3 text-slate-300">
              <a href="#contatti" className="hover:text-pink-400 transition-colors"><Instagram className="h-4 w-4" /></a>
              <a href="#contatti" className="hover:text-pink-400 transition-colors"><Facebook className="h-4 w-4" /></a>
              <a href="#contatti" className="hover:text-pink-400 transition-colors"><TikTok className="h-4 w-4" /></a>
            </div>
            <Link
              to="/dj/login"
              data-testid="home-dj-login-link"
              className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-mono tracking-widest hover:border-pink-500/60 hover:text-pink-400 transition-colors"
            >
              <Headphones className="h-3.5 w-3.5" /> AREA DJ
            </Link>
            <button
              onClick={() => setMenuOpen((o) => !o)}
              data-testid="mobile-menu-button"
              className="lg:hidden h-10 w-10 flex items-center justify-center rounded-lg border border-white/10"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden border-t border-white/5 bg-black/80 px-4 py-3 space-y-1" data-testid="mobile-menu">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.href}
                onClick={() => setMenuOpen(false)}
                className="block py-2 text-sm text-slate-200 hover:text-pink-400"
              >
                {n.label}
              </a>
            ))}
            <Link
              to="/dj/login"
              onClick={() => setMenuOpen(false)}
              data-testid="home-dj-login-link-mobile"
              className="flex items-center gap-2 py-2 text-sm font-mono tracking-widest text-pink-400"
            >
              <Headphones className="h-4 w-4" /> AREA DJ
            </Link>
          </div>
        )}
      </header>

      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${heroSrc})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
          }}
        >
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(90deg, rgba(6,6,9,0.94) 0%, rgba(6,6,9,0.82) 42%, rgba(6,6,9,0.38) 72%, rgba(6,6,9,0.15) 100%)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#060609] via-transparent to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 min-h-[560px] lg:min-h-[640px] flex items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-xl">
            <Crown className="h-10 w-10 text-fuchsia-500 mb-3" style={{ filter: "drop-shadow(0 0 12px rgba(236,72,153,.8))" }} />
            <h1 className="font-brush text-6xl sm:text-7xl lg:text-8xl leading-none">
              DJ <span className="text-white">JEKO</span>
            </h1>
            <p className="mt-4 font-mono text-sm sm:text-base tracking-[0.3em] text-slate-200">
              KARAOKE <span className="text-pink-500">•</span> DJ SET <span className="text-pink-500">•</span> EVENTI
            </p>

            <p className="font-script neon-text-magenta text-4xl sm:text-5xl leading-tight mt-6">
              La tua serata<br />La tua voce<br />Il mio Sound
            </p>

            <p className="mt-6 font-mono text-xs sm:text-sm tracking-[0.25em] text-slate-300">
              MUSICA <span className="text-cyan-400">•</span> DIVERTIMENTO <span className="text-cyan-400">•</span> EMOZIONI
            </p>

            <div className="mt-8 space-y-4 max-w-sm">
              <Link
                to="/prenota"
                data-testid="home-book-cta"
                className="group flex items-center justify-between rounded-full px-7 py-4 font-semibold text-white shadow-lg shadow-pink-900/40 transition active:scale-[0.98]"
                style={{ background: "linear-gradient(90deg,#DB2777,#A855F7)" }}
              >
                <span className="flex items-center gap-3"><CalendarPlus className="h-5 w-5" /> PRENOTA ORA</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/prenota"
                data-testid="home-karaoke-cta"
                className="group flex items-center justify-between rounded-full px-7 py-4 font-semibold border border-white/20 bg-black/30 backdrop-blur-sm hover:border-pink-500/60 transition active:scale-[0.98]"
              >
                <span className="flex items-center gap-3"><Music4 className="h-5 w-5 text-pink-400" /> VAI AL KARAOKE</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>

          <p className="hidden lg:block font-script neon-text-cyan text-4xl xl:text-5xl absolute bottom-10 right-8 text-right leading-tight">
            Good<br />Music &amp;<br />Good People
          </p>
        </div>
      </section>

      {/* ===== Servizi ===== */}
      <section id="servizi" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const Inner = (
              <>
                <div className={`h-14 w-14 rounded-xl bg-black/40 border ${s.ring} flex items-center justify-center mb-5`}>
                  <Icon className={`h-7 w-7 ${s.color}`} />
                </div>
                <h3 className="font-display font-extrabold text-lg mb-2">{s.label}</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-5 min-h-[60px]">{s.desc}</p>
                <span className="inline-flex items-center gap-2 text-xs font-mono tracking-widest rounded-full border border-white/15 px-4 py-2 group-hover:border-pink-500/60 group-hover:text-pink-400 transition-colors">
                  SCOPRI <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </>
            );
            const cls = "jeko-card group rounded-2xl p-6 text-left block";
            return (
              <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}>
                {s.to ? (
                  <Link to={s.to} data-testid={`service-card-${i}`} className={cls}>{Inner}</Link>
                ) : (
                  <a href={s.href} data-testid={`service-card-${i}`} className={cls}>{Inner}</a>
                )}
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ===== QR + Eventi + Crowd ===== */}
      <section id="eventi" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* QR */}
          <div className="jeko-card rounded-2xl p-6 text-center flex flex-col items-center" data-testid="home-qr">
            <p className="font-display font-extrabold text-sm tracking-wide mb-4">
              INQUADRA IL QR CODE<br />E PRENOTA LA TUA CANZONE
            </p>
            <div className="bg-white p-3 rounded-2xl" style={{ boxShadow: "0 0 30px rgba(236,72,153,0.5)" }}>
              <QRCodeCanvas value={bookingUrl} size={180} level="H" fgColor="#08080C" />
            </div>
            <p className="font-script neon-text-magenta text-3xl mt-4">Scan &amp; Sing</p>
          </div>

          {/* Eventi */}
          <div className="jeko-card rounded-2xl p-6" data-testid="home-events">
            <p className="flex items-center gap-2 font-display font-extrabold text-lg mb-5">
              <CalendarDays className="h-5 w-5 text-pink-500" /> PROSSIMI EVENTI
            </p>
            <div className="space-y-2">
              {events.map((e, i) => (
                <div key={`${e.title}-${i}`} className="flex items-center gap-4 rounded-xl bg-black/30 border border-white/5 px-4 py-3 hover:border-pink-500/40 transition-colors">
                  <div className="text-center shrink-0 w-12">
                    <p className="font-mono text-[10px] text-slate-500">{e.w}</p>
                    <p className="font-display font-black text-2xl leading-none text-pink-400">{e.d}</p>
                    <p className="font-mono text-[10px] text-slate-500">{e.m}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{e.title}</p>
                    <p className="text-xs text-slate-400 truncate">{e.place}</p>
                  </div>
                  <span className="hidden sm:block font-mono text-[10px] tracking-widest text-slate-500">DJ JEKO</span>
                  <ArrowRight className="h-4 w-4 text-slate-500 shrink-0" />
                </div>
              ))}
            </div>
            <button
              onClick={() => document.getElementById("contatti")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="home-all-events"
              className="mt-5 w-full flex items-center justify-center gap-2 rounded-full border border-white/15 hover:border-pink-500/60 hover:text-pink-400 py-3 text-sm font-medium transition-colors"
            >
              VEDI TUTTI GLI EVENTI <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {/* Banner */}
          <div className="relative rounded-2xl overflow-hidden border border-white/10 min-h-[280px]">
            <img src={bannerSrc} alt="Banner" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer id="contatti" className="border-t border-white/5 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="flex items-center gap-3">
            <Headphones className="h-8 w-8 text-pink-500" />
            <div>
              <p className="font-brush text-xl">DJ <span className="neon-text-magenta">JEKO</span></p>
              <p className="font-mono text-[9px] tracking-[0.3em] text-slate-500">MUSIC IS LIFE</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Phone className="h-5 w-5 text-pink-400 mt-0.5" />
            <div>
              <p className="font-mono text-[10px] tracking-widest text-slate-500">CONTATTAMI</p>
              <a href={`tel:${(c.phone || "").replace(/\s/g, "")}`} className="text-sm text-slate-200 hover:text-pink-400">{c.phone || "—"}</a>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <MessageCircle className="h-5 w-5 text-emerald-400 mt-0.5" />
            <div>
              <p className="font-mono text-[10px] tracking-widest text-slate-500">WHATSAPP</p>
              {waNumber ? (
                <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer" className="text-sm text-slate-200 hover:text-emerald-400">Scrivimi ora</a>
              ) : (
                <span className="text-sm text-slate-500">—</span>
              )}
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-cyan-400 mt-0.5" />
            <div>
              <p className="font-mono text-[10px] tracking-widest text-slate-500">EMAIL</p>
              <a href={`mailto:${c.email || ""}`} className="text-sm text-slate-200 hover:text-cyan-400">{c.email || "—"}</a>
              <div className="flex items-center gap-3 mt-3 text-slate-300">
                {c.instagram && <a href={c.instagram} target="_blank" rel="noreferrer" className="hover:text-pink-400"><Instagram className="h-4 w-4" /></a>}
                {c.facebook && <a href={c.facebook} target="_blank" rel="noreferrer" className="hover:text-pink-400"><Facebook className="h-4 w-4" /></a>}
                {c.tiktok && <a href={c.tiktok} target="_blank" rel="noreferrer" className="hover:text-pink-400"><TikTok className="h-4 w-4" /></a>}
                {c.youtube && <a href={c.youtube} target="_blank" rel="noreferrer" className="hover:text-pink-400"><Youtube className="h-4 w-4" /></a>}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-center gap-2 text-center">
            <span className="font-brush text-sm">DJ JEKO</span>
            <span className="hidden sm:inline text-slate-600">|</span>
            <span className="font-mono text-[11px] tracking-widest text-slate-500">KARAOKE • DJ SET • EVENTI</span>
            <span className="hidden sm:inline text-slate-600">|</span>
            <span className="font-mono text-[11px] tracking-widest text-slate-500 flex items-center gap-1">
              LA MUSICA È SEMPRE UNA BUONA IDEA <Heart className="h-3 w-3 text-pink-500 fill-pink-500" />
            </span>
            <span className="hidden sm:inline text-slate-600">|</span>
            <span className="font-mono text-[11px] tracking-widest text-slate-500">Cured by il Toscano</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
